import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Heart, Reply, Trash2, ArrowLeft, MessageSquare, Crown } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Reveal from "@/components/Reveal";

const timeAgo = (iso) => { const d = (Date.now() - new Date(iso)) / 1000; if (d < 60) return "just now"; if (d < 3600) return `${Math.floor(d / 60)}m ago`; if (d < 86400) return `${Math.floor(d / 3600)}h ago`; return `${Math.floor(d / 86400)}d ago`; };

function Avatar({ name, picture }) {
  return picture ? (
    <img src={picture} alt={name} className="w-10 h-10 rounded-full object-cover border border-gold/40" />
  ) : (
    <div className="w-10 h-10 rounded-full border border-gold/40 bg-mahogany-800 flex items-center justify-center text-gold font-serif">{(name || "?")[0]}</div>
  );
}

function CommentBody({ c, isReply, userId, isAdmin, onLike, onDelete }) {
  const liked = c.likes?.includes(userId);
  return (
    <div className="flex gap-4">
      <Link to={`/members/${c.user_id}`} data-testid={`comment-avatar-${c.id}`}><Avatar name={c.name} picture={c.picture} /></Link>
      <div className="flex-1">
        <div className="bg-mahogany-800/50 gold-frame rounded-sm p-5">
          <div className="flex items-center justify-between mb-2">
            <Link to={`/members/${c.user_id}`} className="font-serif text-lg text-cream hover:text-gold inline-flex items-center gap-1.5" data-testid={`comment-author-${c.id}`}><Crown size={12} className="text-gold" /> {c.name}</Link>
            <span className="text-cream/40 text-xs">{timeAgo(c.created_at)}</span>
          </div>
          <p className="text-cream/75 leading-relaxed whitespace-pre-line">{c.text}</p>
        </div>
        <div className="flex items-center gap-5 mt-2 px-1">
          <button onClick={() => onLike(c.id)} className={`flex items-center gap-1.5 text-xs ${liked ? "text-fuchsia" : "text-cream/50 hover:text-fuchsia"}`} data-testid={`like-${c.id}`}>
            <Heart size={14} fill={liked ? "#FF1493" : "none"} /> {c.likes?.length || 0}
          </button>
          {(c.user_id === userId || isAdmin) && <button onClick={() => onDelete(c.id)} className="flex items-center gap-1.5 text-xs text-cream/40 hover:text-red-400" data-testid={`delete-${c.id}`}><Trash2 size={13} /></button>}
        </div>
      </div>
    </div>
  );
}

export default function BookDiscussion() {
  const { id } = useParams();
  const { user } = useAuth();
  const [book, setBook] = useState(null);
  const [prompt, setPrompt] = useState(null);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState("");

  const load = () => {
    api.get(`/books/${id}`).then(({ data }) => setBook(data));
    api.get(`/books/${id}/comments`).then(({ data }) => setComments(data || []));
    api.get("/prompts/current").then(({ data }) => setPrompt(data && data.id ? data : null));
  };
  useEffect(() => { load(); }, [id]);

  const post = async (body) => { const { data } = await api.post(`/books/${id}/comments`, body); setComments((prev) => [...prev, data]); };
  const submit = async (e) => { e.preventDefault(); if (!text.trim()) return; await post({ text, prompt_id: prompt?.id || null }); setText(""); toast.success("Posted."); };
  const onReply = async (parentId) => { if (!replyText.trim()) return; await post({ text: replyText, parent_id: parentId }); setReplyText(""); setReplyTo(null); };
  const onLike = async (cid) => { const { data } = await api.post(`/comments/${cid}/like`); setComments((prev) => prev.map((c) => c.id === cid ? { ...c, likes: data.likes } : c)); };
  const onDelete = async (cid) => { await api.delete(`/comments/${cid}`); setComments((prev) => prev.filter((c) => c.id !== cid && c.parent_id !== cid)); toast("Deleted."); };

  const roots = comments.filter((c) => !c.parent_id);
  const repliesOf = (pid) => comments.filter((c) => c.parent_id === pid);

  if (!book) return <div className="min-h-screen bg-mahogany-900" />;

  return (
    <div className="min-h-screen bg-gradient-to-b from-mahogany-950 to-mahogany-900 pt-32 pb-20 px-6 grain" data-testid="discussion-page">
      <div className="max-w-3xl mx-auto">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-cream/60 hover:text-gold text-sm mb-8" data-testid="back-dashboard"><ArrowLeft size={16} /> Back to your room</Link>

        <Reveal className="flex gap-6 items-center mb-10">
          <img src={book.cover_url} alt={book.title} className="w-24 rounded-sm shadow-lg" />
          <div>
            <p className="overline mb-1">The Discussion</p>
            <h1 className="font-serif text-4xl text-cream leading-tight">{book.title}</h1>
            <p className="text-caramel">by {book.author}</p>
          </div>
        </Reveal>

        {prompt && prompt.book_id === book.id && (
          <Reveal className="bg-gradient-to-br from-fuchsia/20 to-mahogany-950 gold-frame rounded-sm p-7 mb-10" data-testid="discussion-prompt">
            <p className="overline mb-3 flex items-center gap-2"><MessageSquare size={13} /> Discussion Prompt · {prompt.month}</p>
            <p className="font-serif text-2xl text-cream leading-snug">"{prompt.question}"</p>
          </Reveal>
        )}

        <form onSubmit={submit} className="mb-12" data-testid="comment-form">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Share your thoughts with the room..." className="input-line resize-none mb-3" data-testid="comment-input" />
          <div className="flex justify-end"><button type="submit" className="btn-primary" data-testid="comment-submit">Post to the Room</button></div>
        </form>

        <div className="space-y-8">
          {roots.length ? roots.map((c) => (
            <div key={c.id} data-testid={`comment-${c.id}`}>
              <CommentBody c={c} isReply={false} userId={user?.id} isAdmin={user?.role === "admin"} onLike={onLike} onDelete={onDelete} />
              <div className="ml-14">
                <div className="flex items-center gap-5 mt-2 px-1">
                  <button onClick={() => setReplyTo(replyTo === c.id ? null : c.id)} className="flex items-center gap-1.5 text-xs text-cream/50 hover:text-gold" data-testid={`reply-${c.id}`}><Reply size={14} /> Reply</button>
                </div>
                {replyTo === c.id && (
                  <div className="flex gap-2 mt-3">
                    <input value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Write a reply..." className="input-line flex-1 !py-2" data-testid={`reply-input-${c.id}`} />
                    <button onClick={() => onReply(c.id)} className="btn-primary !px-5 !py-2 !text-xs" data-testid={`reply-submit-${c.id}`}>Send</button>
                  </div>
                )}
                {repliesOf(c.id).map((r) => (
                  <div key={r.id} className="mt-4" data-testid={`comment-${r.id}`}>
                    <CommentBody c={r} isReply userId={user?.id} isAdmin={user?.role === "admin"} onLike={onLike} onDelete={onDelete} />
                  </div>
                ))}
              </div>
            </div>
          )) : <p className="text-cream/50 text-center py-10">Be the first to share your thoughts.</p>}
        </div>
      </div>
    </div>
  );
}
